import { useNavigate } from "@tanstack/react-router";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/errors";
import { useAuth } from "@/shared/auth";
import { toast } from "@/shared/lib/toast";

import { useChangePassword } from "../api/use-change-password";
import { ChangePasswordForm } from "../components/ChangePasswordForm";
import type { ChangePasswordFormValues } from "../lib/change-password-schema";

export function ChangePasswordPage(): JSX.Element {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { mutate, isPending, error } = useChangePassword();

  function handleSubmit(values: ChangePasswordFormValues): void {
    mutate(
      { old_password: values.old_password, new_password: values.new_password },
      {
        onSuccess: () => {
          toast.success(t("auth:change_password.success"));
          logout();
          void navigate({ to: "/login" });
        },
        onError: (err) => {
          if (!(err instanceof ApiError && err.status === 401)) {
            toast.error(t("common:errors.unexpected"));
          }
        },
      },
    );
  }

  const serverError =
    error instanceof ApiError && error.status === 401
      ? t("auth:error.old_password_invalid")
      : undefined;

  return (
    <main
      className="flex min-h-screen items-center justify-center bg-[var(--surface-page)]"
      data-testid="change-password-page"
    >
      <div className="w-full max-w-sm rounded-xl border border-[var(--border-default)] bg-white p-8 shadow-[var(--sh-2)]">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-bold text-[var(--text-primary)]">
            {t("auth:change_password.title")}
          </h1>
          <p className="mt-1 text-sm text-[var(--text-subtle)]">
            {t("auth:change_password.subtitle")}
          </p>
        </div>
        <ChangePasswordForm
          onSubmit={handleSubmit}
          isLoading={isPending}
          {...(serverError ? { serverError } : {})}
        />
      </div>
    </main>
  );
}
